import {
    Dispatch,
    FC,
    SetStateAction,
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';

import { useLazyQuery } from '@apollo/client';
import { filter, flatMap, isEmpty, uniqBy } from '@npi/buddy-utils';
import { PricingType } from '@prisma/client';
import { PartnerOffers } from 'types/graphql';

import { showErrorToast } from 'src/components/Toasts/toast';
import { GET_PARTNERS_OFFERS } from 'src/graphql/queries/partners.query';
import { TransactionPricingOffer } from 'src/types/AddPartner/pricingInfoStep4';

import { useAddPartnerContext } from '../AddPartnerProvider';

interface AssignOffersContext {
    fetchPartnerOffers: () => void;
    setSelectedOffer: Dispatch<SetStateAction<{ offer: PartnerOffers; type: PricingType }>>;
    selectedOffer: { offer: PartnerOffers; type: PricingType };
    handleAssignOffer: () => void;
    handleUnassignOffer: () => void;
    fetchingOffers: boolean;

    assignedTerminalAndOnlineOffers: PartnerOffers[];
    assignedTransactionOffers: TransactionPricingOffer[];

    transactionOffers: TransactionPricingOffer[];
    terminalAndOnlineOffers: PartnerOffers[];
    assignedOfferIds: number[];
    assignedTransactionOfferIds: number[];
    setAssignedTransactionOffers: Dispatch<SetStateAction<TransactionPricingOffer[]>>;
    setTransactionOffers: Dispatch<SetStateAction<TransactionPricingOffer[]>>;
}

const AssignOffersContext = createContext<Partial<AssignOffersContext>>({});

export const useAssignOffersProvider = () => {
    return useContext(AssignOffersContext);
};

export const AssignOffersProvider: FC = ({ children }) => {
    const { application } = useAddPartnerContext();
    const [selectedOffer, setSelectedOffer] = useState<{
        offer: PartnerOffers | TransactionPricingOffer;
        type: PricingType;
    }>(null);

    const [assignedTerminalAndOnlineOffers, setAssignedTerminalAndOnlineOffers] = useState<
        PartnerOffers[]
    >([]);

    const [assignedOfferIds, setAssignedOfferIds] = useState<number[]>();
    const [assignedTransactionOfferIds, setAssignedTransactionOfferIds] = useState<number[]>();

    const [assignedTransactionOffers, setAssignedTransactionOffers] = useState<
        TransactionPricingOffer[]
    >([]);

    const [terminalAndOnlineOffers, setTerminalAndOnlineOffers] = useState<PartnerOffers[]>();

    const [transactionOffers, setTransactionOffers] = useState<TransactionPricingOffer[]>();

    const [fetchOffers, { loading: fetchingOffers }] = useLazyQuery(GET_PARTNERS_OFFERS, {
        onError: () => {
            return showErrorToast('There was a problem getting the offers');
        },
    });

    const filterAssignedOffers = (offers, assignedOffers) => {
        // Create a set of assignedOffer ids for faster lookup
        const assignedOfferIds = new Set(
            assignedOffers.map((assignedOffer) => {
                return assignedOffer.id;
            })
        );

        // Filter the offers array
        const filteredOffers = offers.filter((offer) => {
            return !assignedOfferIds.has(offer.id);
        });

        return filteredOffers;
    };

    const fetchPartnerOffers = async () => {
        let partnerId;

        if (application?.partnerType === 'PARTNER_OF_PARTNER') {
            partnerId = application?.partnerId;
        }
        // if partner id provided then fetch offers for given partner else return all offers
        const { data } = await fetchOffers({ variables: { id: partnerId } });

        if (isEmpty(data?.partnersOffers)) {
            return;
        }

        // If the current application contains assigned offers, set the relevant states
        if (application?.assignedOffers) {
            setAssignedTerminalAndOnlineOffers(application?.assignedOffers);
            // extract the already assigned offers from the total available offers and set the respective states
            const availableOffers = filterAssignedOffers(
                data?.partnersOffers,
                application?.assignedOffers
            );

            setTerminalAndOnlineOffers(availableOffers);
        } else {
            setTerminalAndOnlineOffers(data?.partnersOffers);
        }

        // Flatten the transaction pricing offers
        const allTransactionPricingOffers = flatMap(data?.partnersOffers, (offer) => {
            return (offer as any).transactionPricing;
        });

        // Remove all duplicates. (The same transaction pricing offer can be assigned to multiple offers and offer types)
        const transactionPricingOffers = uniqBy(allTransactionPricingOffers, 'id' as any);

        // if the current application contains assigned transaction offers, set the relevant states
        if (application?.assignedTransactionOffers) {
            setAssignedTransactionOffers(application?.assignedTransactionOffers);
            // Remove the already assigned offers from the total available offers
            const availableTransactionOffers = filterAssignedOffers(
                transactionPricingOffers,
                application?.assignedTransactionOffers
            );

            setTransactionOffers(availableTransactionOffers);
        } else {
            setTransactionOffers(transactionPricingOffers);
        }
    };

    const handleAssignOffer = () => {
        const { offer, type } = selectedOffer;

        if (type === PricingType.TRANSACTIONAL) {
            setAssignedTransactionOffers((prev: TransactionPricingOffer[]) => {
                return [...prev, offer as TransactionPricingOffer];
            });

            setTransactionOffers((prev) => {
                return filter(prev, (availableOffer) => {
                    return availableOffer.id !== offer.id;
                });
            });
            return;
        }

        setAssignedTerminalAndOnlineOffers((prev: PartnerOffers[]) => {
            return [...prev, offer as PartnerOffers];
        });

        setTerminalAndOnlineOffers((prev) => {
            return filter(prev, (availableOffer) => {
                return availableOffer.id !== offer.id;
            });
        });
    };

    const handleUnassignOffer = () => {
        const { offer, type } = selectedOffer;

        if (type === PricingType.TRANSACTIONAL) {
            setTransactionOffers((prev: TransactionPricingOffer[]) => {
                return [...prev, offer as TransactionPricingOffer];
            });

            setAssignedTransactionOffers((prev) => {
                return filter(prev, (availableOffer) => {
                    return availableOffer.id !== offer.id;
                });
            });
            return;
        }

        setTerminalAndOnlineOffers((prev: PartnerOffers[]) => {
            return [...prev, offer as PartnerOffers];
        });

        setAssignedTerminalAndOnlineOffers((prev) => {
            return filter(prev, (availableOffer) => {
                return availableOffer.id !== offer.id;
            });
        });
    };

    useEffect(() => {
        setAssignedOfferIds(
            assignedTerminalAndOnlineOffers.map((offer) => {
                return offer.id;
            })
        );
    }, [assignedTerminalAndOnlineOffers]);

    useEffect(() => {
        setAssignedTransactionOfferIds(
            assignedTransactionOffers.map((offer) => {
                return offer.id;
            })
        );
    }, [assignedTransactionOffers]);

    return (
        <AssignOffersContext.Provider
            value={{
                fetchPartnerOffers,
                setSelectedOffer,
                selectedOffer,
                handleAssignOffer,
                handleUnassignOffer,
                fetchingOffers,

                assignedTerminalAndOnlineOffers,
                assignedTransactionOffers,

                transactionOffers,
                terminalAndOnlineOffers,
                assignedOfferIds,
                assignedTransactionOfferIds,

                setAssignedTransactionOffers,
                setTransactionOffers,
            }}
        >
            {children}
        </AssignOffersContext.Provider>
    );
};
